/**
 * Meta (Facebook) Pixel — WEB ONLY.
 *
 * Loads the browser pixel and exposes a tiny track() helper. Deliberately a
 * no-op in the native iOS app (App Store "reader app" + ATT rules) and when a
 * visitor has opted out via the consent banner.
 *
 * Standard events we fire:
 *   • PageView  — on load (here)
 *   • Lead      — lead-magnet email-gate submit (MasterclassEmailGate)
 *   • InitiateCheckout — "Get access" on the landing page (LandingPage),
 *                 and "Enroll" on the cert page (CertSalesPage)
 *   • ViewContent — the public cert page (CertSalesPage)
 *   • Advantage Flow (2026-11-03, brief 774): ViewContent on /afs, Lead when
 *                 Get started or the quote form is sent, InitiateCheckout on
 *                 "Continue to payment", StartTrial on a claimed trial, and
 *                 Purchase once the checkout is confirmed (event id = the
 *                 Stripe session id, which the webhook sends server-side too).
 *                 See src/dashboard/film/afsFunnel.js.
 *   • Purchase  — server-side only now (Stripe webhook, Conversions API):
 *                 the success page is inside the platform, where the
 *                 pixel no longer runs (PQ-12, 2026-10-19).
 *
 * Pixel ID is public by design (it ships in the page). The CAPI access token
 * is NOT here — that lives server-side in the Stripe webhook as a secret.
 */
import { isNative } from "./platform";
import { IS_STAGING } from "../config/environment";

const PIXEL_ID = "779867197802146";
const OPTOUT_KEY = "bam_meta_optout";

let loaded = false;

// Staging never reports to the production pixel (D138), so it counts as opted out.
function optedOut() {
  if (IS_STAGING) return true;
  try { return localStorage.getItem(OPTOUT_KEY) === "1"; } catch { return false; }
}

// Where the pixel may run (Coleman, 2026-10-19, PQ-12): the public marketing
// and sign-up surfaces only. Never inside the platform, where coaches type
// about players (Dev Studio, BAM IQ, film). A Purchase made after checkout is
// still counted: the Stripe webhook sends it to Meta server-side (Conversions
// API), and the Paywall passes the _fbp/_fbc cookies along for matching.
//
// The path matters as well as the hash (2026-11-03, brief 774). Advantage
// Flow's own page lives at the path /afs, with any hash ("/afs#plans"). Every
// other hash route is on the root path. A shared game (/g/<token>) or a
// player's page (/p/<token>) has an empty hash too, and those are coach-made
// pages with players on them, so an empty hash only counts on the root.
const AFS_PATH = /^\/afs\/?$/;
const ROOT_PATH = /^\/(index\.html)?$/;
// #/afs-start is the Advantage Flow plan, trial and checkout page. A tagger's
// or a staff invite also opens there: those aren't ad traffic, so not those.
const AFS_START_INTERNAL = /[?&](tagger|staff)=/;

export function isMarketingRoute(hash, pathname = "/") {
  const path = String(pathname || "/");
  if (AFS_PATH.test(path)) return true;
  if (!ROOT_PATH.test(path)) return false;
  const full = String(hash || "");
  const h = full.split("?")[0];
  // Advantage Flow's start and terms pages (2026-11-03): ads for it land on
  // /afs and the visitor buys on #/afs-start.
  if (h === "#/afs-start" || h === "#/afs-start/") return !AFS_START_INTERNAL.test(full);
  if (h === "#/afs-terms") return true;
  // The cert sales and checkout pages count as marketing (2026-11-02): paid
  // ads send people there, and without the pixel Meta can't see who views or
  // starts a cert checkout. Neither page has anything a coach types.
  return h === "" || h === "#" || h === "#/" || h === "#/landing" || h === "#/signup" || h.startsWith("#/free/")
    || h === "#/certification" || h === "#/buy-cert" || h === "#/cert-path";
}

const routeNow = () => (typeof window !== "undefined" ? [window.location.hash, window.location.pathname] : ["", "/"]);
const onMarketingRoute = () => isMarketingRoute(...routeNow());

// Load the pixel on a marketing route; on any other route, tell an already
// loaded pixel to stop sending (consent revoke), and resume when the visitor
// comes back to a marketing page. Call on load and on every hashchange.
export function syncMetaPixelToRoute() {
  if (isNative() || optedOut() || typeof window === "undefined") return;
  if (onMarketingRoute()) {
    if (!loaded) { initMetaPixel(); return; }
    // A jump to a section of the same page (/afs#plans) isn't a new page.
    const h = window.location.hash || "";
    try { window.fbq("consent", "grant"); if (!h || h.startsWith("#/")) window.fbq("track", "PageView"); } catch {}
  } else if (loaded) {
    try { window.fbq("consent", "revoke"); } catch {}
  }
}

// Web-only, idempotent. Injects the pixel and fires PageView. Skipped in the
// native app, for opted-out visitors and off the marketing routes.
export function initMetaPixel() {
  if (!onMarketingRoute()) return;
  if (loaded || isNative() || optedOut()) return;
  if (typeof window === "undefined" || typeof document === "undefined") return;
  /* eslint-disable */
  !function (f, b, e, v, n, t, s) {
    if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments) };
    if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = "2.0"; n.queue = [];
    t = b.createElement(e); t.async = !0; t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  }(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */
  try {
    window.fbq("init", PIXEL_ID);
    window.fbq("track", "PageView");
    loaded = true;
  } catch { /* never break the app for analytics */ }
}

// Fire a standard event. Pass eventId to enable browser↔CAPI dedup (Purchase
// uses the Stripe session id so the browser + server events collapse to one).
export function metaTrack(event, params = {}, eventId) {
  if (isNative() || optedOut() || !onMarketingRoute()) return;
  try {
    if (typeof window === "undefined" || typeof window.fbq !== "function") return;
    if (eventId) window.fbq("track", event, params, { eventID: eventId });
    else window.fbq("track", event, params);
  } catch { /* swallow */ }
}

/**
 * The two cookies Meta cares most about, and the single biggest lever on
 * match quality for a server-side event.
 *
 *   _fbp  the browser id the pixel writes on first load
 *   _fbc  the click id, written when someone lands with ?fbclid=...
 *
 * A Purchase that carries an email and nothing else scores badly because
 * an email is one weak signal and half the internet shares an address
 * with nobody. fbp and fbc tie the conversion to the actual browser that
 * saw the actual ad, which is what Meta is really asking for.
 *
 * The fbclid fallback matters: the pixel writes _fbc itself, but only
 * once it has loaded, and only if it loaded at all. If a tracker
 * blocker ate fbevents.js, or the visitor converted before the script
 * finished, the click id is sitting right there in the URL and nobody
 * has written it down. So we write it down, in Meta's own
 * `fb.1.<ms>.<fbclid>` format, and persist it for the 90-day window
 * they use.
 */
function readCookie(name) {
  try {
    const m = document.cookie.match(new RegExp("(^|;\\s*)" + name + "=([^;]*)"));
    return m ? decodeURIComponent(m[2]) : null;
  } catch { return null; }
}

const FBC_MAX_AGE = 90 * 24 * 60 * 60; // Meta's click-attribution window.

export function getFbCookies() {
  if (typeof document === "undefined") return { fbp: null, fbc: null };
  const fbp = readCookie("_fbp");
  let fbc = readCookie("_fbc");
  if (!fbc) {
    try {
      const fbclid = new URLSearchParams(window.location.search).get("fbclid");
      if (fbclid) {
        fbc = `fb.1.${Date.now()}.${fbclid}`;
        // Same name and shape the pixel would have used, so when it does
        // load it finds its own cookie rather than writing a second one.
        document.cookie = `_fbc=${encodeURIComponent(fbc)}; max-age=${FBC_MAX_AGE}; path=/; SameSite=Lax`;
      }
    } catch { /* no URL, no click id, nothing lost */ }
  }
  return { fbp: fbp || null, fbc: fbc || null };
}

// Everything the server needs to describe this browser on a CAPI event.
// The IP has to come from the request headers server-side - a page
// cannot know its own public address.
export function getMetaContext() {
  const { fbp, fbc } = getFbCookies();
  return {
    fbp,
    fbc,
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    eventSourceUrl: typeof window !== "undefined" ? window.location.href : null,
  };
}

// Record an opt-out (consent banner "Decline"). Future loads skip the pixel
// and we revoke consent for the current session if it already loaded.
export function setMetaOptOut(out) {
  try { localStorage.setItem(OPTOUT_KEY, out ? "1" : "0"); } catch {}
  if (out) { try { if (typeof window !== "undefined" && window.fbq) window.fbq("consent", "revoke"); } catch {} }
}
export function hasMetaOptOut() { return optedOut(); }
